import { SectionCard, fmtNum, fmtTRY } from '../yeni-design/primitives';
import type { Donem } from './donem';
import type { SaKaynak } from './ekranKaynaklari';
import { parametreBildiriyor, useSaSorgu } from './useSatisVerisi';
import { KapsamNotu } from './KapsamNotu';
import { BlokIskeleti } from './BlokIskeleti';
import { sayi, type SaKategoriSatiri } from './ozetHesaplari';
import { oranMetni } from './bicim';

export const KATEGORI_ILK_N = 8;

export interface KategoriTabloSatiri {
  ad: string;
  ciro: number;
  pay: number;
  marj: number | null;
  aUrun: number;
  aCiroPay: number | null;
  diger: boolean;
}

/**
 * Ciroya gore ilk N kategori + kalanlarin tek "Diger N kategori" satiri. A ciro payi Diger satirinda
 * kategori cirolariyla AGIRLIKLI toplanir (oranlarin ortalamasi kucuk kategoriyi buyuk gosterirdi).
 * Tablo CARI donemin tablosudur: cari cirosu NULL/undefined (990051'in yalniz-onceki satiri, final-fix F-1) olan
 * kategori ne satir ne "Diger N" sayisina girer. Cirosu TAM 0 olan kategori (K7/S5) satir olarak KALIR — 0 dusurulmez.
 */
function kategoriler(rows: SaKategoriSatiri[]) {
  return rows
    // K7/S5 (spec §9): ciro TAM 0 NULL/undefined DEGILDIR — satis oldu, tutari sifir. Yalniz cari cirosu
    // hic yoken (990051'in yalniz-onceki satiri, final-fix F-1) tablodan dusurulur.
    .filter((r) => r.tur === 'kategori' && r.ciro != null)
    .map((r) => ({
      ad: String(r.ad),
      ciro: sayi(r.ciro),
      kar: sayi(r.kar),
      pay: sayi(r.pay),
      aUrun: sayi(r.a_urun),
      aCiroPay: r.a_ciro_pay === null || r.a_ciro_pay === undefined ? null : sayi(r.a_ciro_pay),
    }))
    .sort((a, b) => b.ciro - a.ciro);
}

type Kategori = ReturnType<typeof kategoriler>[number];

const tabloSatiri = (k: Kategori): KategoriTabloSatiri => ({
  ad: k.ad,
  ciro: k.ciro,
  pay: k.pay,
  marj: k.ciro !== 0 ? (k.kar / k.ciro) * 100 : null,
  aUrun: k.aUrun,
  aCiroPay: k.aCiroPay,
  diger: false,
});

export function kategoriTablosu(rows: SaKategoriSatiri[], ilkN = KATEGORI_ILK_N): KategoriTabloSatiri[] {
  const kat = kategoriler(rows);
  const bas = kat.slice(0, ilkN).map(tabloSatiri);
  const kalan = kat.slice(ilkN);
  if (kalan.length === 0) return bas;

  const ciro = kalan.reduce((t, k) => t + k.ciro, 0);
  const kar = kalan.reduce((t, k) => t + k.kar, 0);
  const aCiro = kalan.reduce((t, k) => t + ((k.aCiroPay ?? 0) / 100) * k.ciro, 0);
  return [
    ...bas,
    {
      ad: `Diğer ${kalan.length} kategori`,
      ciro,
      pay: kalan.reduce((t, k) => t + k.pay, 0),
      marj: ciro !== 0 ? (kar / ciro) * 100 : null,
      aUrun: kalan.reduce((t, k) => t + k.aUrun, 0),
      aCiroPay: ciro !== 0 ? (aCiro / ciro) * 100 : null,
      diger: true,
    },
  ];
}

/**
 * S295 (kullanici 2026-09-23): "Diger N kategori" satirinin ICINDEKILER, ciroya gore. Ayni suzgec ve siralama
 * `kategoriTablosu`ndan gelir: modaldaki toplam tablodaki Diger satiriyla birebir tutar.
 */
export function digerKategoriler(rows: SaKategoriSatiri[], ilkN = KATEGORI_ILK_N): KategoriTabloSatiri[] {
  return kategoriler(rows).slice(ilkN).map(tabloSatiri);
}

interface Props {
  kaynak: SaKaynak | undefined;
  donem: Donem;
  /** K10: 990051 anahtari OzetSerit/UyarilarBlok ile ayni kalsin diye onceki pencere de parametrede. */
  oncekiDonem: Donem;
  sube?: string | null;
  kanal?: string | null;
  /** K7 (G2b): kategori adi dugmesi detay modalini acar. Verilmezse ad duz metindir. */
  onKategoriSec?: (kategori: string) => void;
  /** S295: "Diger N kategori" satiri dugmesi gizlenen kategorilerin modalini acar. Verilmezse duz metin. */
  onDigerSec?: () => void;
}

/**
 * Satir 3 — kategori tablosu (990051, urun tabani). K7 (G2b): satir TIKLANMAZ; kategori adi dugmesi
 * (onKategoriSec) detay modalini acar. Eski acilir urun kirilimi ve 014/016/017/018 cagrilari KALKTI.
 * ABC: secili donem/sube/kanal kitlesinde.
 */
export function KategoriBlok({ kaynak, donem, oncekiDonem, sube = null, kanal = null, onKategoriSec, onDigerSec }: Props) {
  const q = useSaSorgu<SaKategoriSatiri>(kaynak, { donem, sube, kanal, onceki: oncekiDonem });

  if (!kaynak) return null;
  if (q.isLoading)
    return <BlokIskeleti testid="blok-kategori-iskelet" baslik="Kategori bazında satış" yukseklik={360} satir={8} />;
  const satirlar = kategoriTablosu(q.data?.data ?? []);
  if (satirlar.length === 0) return null;

  return (
    <div data-testid="blok-kategori">
      <SectionCard
        title="Kategori bazında satış"
        subtitle={
          <>
            İlk {KATEGORI_ILK_N} kategori · A: cironun %80&apos;ini yapan ürünler
            <KapsamNotu sube={sube} destekli={parametreBildiriyor(kaynak, '@LOKASYON')} />
          </>
        }
      >
        <div className="overflow-x-auto">
          <table className="w-full text-[13px] border-collapse">
            <thead>
              <tr className="text-[12px] text-ink-500 border-b border-border">
                <th className="text-left font-medium py-2 pr-3">Kategori</th>
                <th className="text-right font-medium py-2 px-3">Ciro</th>
                <th className="text-right font-medium py-2 px-3">Pay</th>
                <th className="text-right font-medium py-2 px-3">Marj</th>
                <th className="text-left font-medium py-2 pl-3">A ürün · ciro payı</th>
              </tr>
            </thead>
            <tbody>
              {satirlar.map((s) => {
                const cubuk = s.aCiroPay === null ? 0 : Math.max(0, Math.min(100, s.aCiroPay));
                return (
                  <tr
                    key={s.ad}
                    data-testid={s.diger ? 'kategori-diger' : `kategori-satir-${s.ad}`}
                    className={`border-b border-border last:border-b-0 ${s.diger ? 'text-ink-500' : ''}`}
                  >
                    <td className="py-2 pr-3 text-ink-900 font-medium truncate">
                      {onKategoriSec && !s.diger ? (
                        <button
                          type="button"
                          data-testid={`kategori-detay-${s.ad}`}
                          onClick={() => onKategoriSec(s.ad)}
                          className="focus-ring max-w-full truncate text-left hover:underline underline-offset-2"
                        >
                          {s.ad}
                        </button>
                      ) : onDigerSec && s.diger ? (
                        <button
                          type="button"
                          data-testid="kategori-diger-detay"
                          onClick={onDigerSec}
                          className="focus-ring max-w-full truncate text-left hover:underline underline-offset-2"
                        >
                          {s.ad}
                        </button>
                      ) : (
                        s.ad
                      )}
                    </td>
                    <td className="py-2 px-3 text-right tnum text-ink-900">{fmtTRY(s.ciro)}</td>
                    <td className="py-2 px-3 text-right tnum text-ink-500">{oranMetni(s.pay, 1)}</td>
                    <td className={`py-2 px-3 text-right tnum ${s.marj !== null && s.marj < 0 ? 'text-neg-700' : 'text-ink-500'}`}>
                      {s.marj !== null ? oranMetni(s.marj, 1) : '—'}
                    </td>
                    <td className="py-2 pl-3">
                      <div className="flex items-center gap-2" title={s.aCiroPay === null ? '' : `A ürünleri kategori cirosunun ${oranMetni(s.aCiroPay, 1)}`}>
                        <span className="tnum text-ink-700 w-8 text-right">{fmtNum(s.aUrun)}</span>
                        <div className="h-1.5 flex-1 min-w-[48px] rounded-full bg-border-light overflow-hidden">
                          <div
                            data-testid={`a-ciro-pay-${s.diger ? 'diger' : s.ad}`}
                            className="h-full rounded-full bg-ink-700"
                            style={{ width: `${cubuk}%` }}
                          />
                        </div>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </SectionCard>
    </div>
  );
}
